import { useState } from 'react';
import { Folder } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateFolder } from '@/hooks/folders';
import { RenderIf } from '@/components/RenderIf';
import type { CreateFolderModalProps } from './types';

const getEntityLabel = (entityType: string): string => {
    switch (entityType) {
        case 'tool':
            return 'tools';
        case 'toolbox':
            return 'toolboxes';
        case 'prompt':
            return 'prompts';
        case 'workflow':
        default:
            return 'workflows';
    }
};

const CreateFolderModal = ({
    isOpen,
    onClose,
    agentId,
    entityType,
    currentPath,
}: CreateFolderModalProps) => {
    const [folderName, setFolderName] = useState('');
    const { createFolder, isCreating, error } = useCreateFolder({ agentId, entityType });
    const entityLabel = getEntityLabel(entityType);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const trimmedName = folderName.trim();
        if (!trimmedName) return;

        // Build the full path
        const fullPath = currentPath ? `${currentPath}/${trimmedName}` : `/${trimmedName}`;

        await createFolder({
            path: fullPath,
            name: trimmedName,
        });

        setFolderName('');
        onClose();
    };

    const handleClose = () => {
        setFolderName('');
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Folder className="h-5 w-5 text-amber-500" />
                            Create Folder
                        </DialogTitle>
                        <DialogDescription>
                            Create a new folder to organize your {entityLabel}.
                            <RenderIf isTrue={!!currentPath}>
                                <span className="block mt-1 text-xs">
                                    Creating in:{' '}
                                    <code className="bg-gray-100 px-1 rounded">{currentPath}</code>
                                </span>
                            </RenderIf>
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="folderName">Folder name</Label>
                            <Input
                                id="folderName"
                                placeholder="Enter folder name"
                                value={folderName}
                                onChange={event => setFolderName(event.target.value)}
                                autoFocus
                            />
                        </div>

                        <RenderIf isTrue={!!error}>
                            <p className="text-sm text-red-500">{error?.message}</p>
                        </RenderIf>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!folderName.trim() || isCreating}>
                            <RenderIf isTrue={isCreating}>Creating...</RenderIf>
                            <RenderIf isTrue={!isCreating}>Create Folder</RenderIf>
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CreateFolderModal;
